/* global google */
import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { includes } from 'lodash';
import debounce from 'lodash.debounce';
import {
  enableShapeSelection,
  openAreaTray,
  setDrawingDone,
  setDrawingPath,
  setViewport,
  viewEntityOnMap,
  viewGroupOnMap,
  viewMoratoriumOnMap
} from '@actions/map-actions';
import { setInitialEntityBounds } from '@actions/entities-actions';
import LayersMenu from '@components/map/layers-menu';
import { isWorkflowEnabled } from '@constants/config';
import DotMapsGoogleMap from '@shared/dotmaps-google-map';
import AddToGroupDialog from '@shared/dialogs/add-to-group-dialog';
import StartCycleDialog from '@shared/dialogs/start-cycle-dialog';
import FilePreview from '@shared/files/file-preview';
import {
  didViewportChanged,
  renderHighlightCircle,
  segmentsToBounds,
  setMapStreetViewPosition
} from '@utils/map-utils';
import {
  addVertex,
  initializeMeasurement,
  onInteractiveStop,
  onMeasurementMouseMove
} from '@utils/measurement-utils';
import { isAppAdmin } from '@utils/permission-utils';
import { shallowEqual } from '@utils/react-utils';
import Components from './components';
import MapDateRangeDialog from './dialogs/date-range-dialog';
import FloatingBar from './floating-bar';
import Legend from './legend';
import Loader from './loader';
import TabBar from './tab-bar';
import TabBarToggle from './tab-bar-toggle';
import Tray from './tray';
import UnstyledHeading from '@components/shared/unstyled-heading';
import './map.scss';

/**
 * Custom comparison function for React.memo.
 * Returns true if props are equal (should NOT re-render).
 * @param {object} prevProps - Previous props.
 * @param {object} nextProps - Next props.
 * @returns {boolean} Whether props are equal.
 */
const arePropsEqual = (prevProps, nextProps) => {
  // Re-render if viewport changed
  if (didViewportChanged(nextProps.viewport, prevProps.viewport)) {
    return false;
  }
  // Re-render if filters changed
  if (!shallowEqual(nextProps.filters, prevProps.filters)) {
    return false;
  }
  // Re-render if agencyId changed
  if (!shallowEqual(nextProps.agencyId, prevProps.agencyId)) {
    return false;
  }
  // Re-render if location changed
  if (!shallowEqual(nextProps.location, prevProps.location)) {
    return false;
  }
  // Re-render if drawing state changed (mode, done, path)
  // This is needed because callbacks access drawing and would have stale closures otherwise.
  // Note: The original class component didn't include this in shouldComponentUpdate,
  // but class components access this.props which is always current.
  // Functional components with useCallback capture values in closures.
  if (!shallowEqual(nextProps.drawing, prevProps.drawing)) {
    return false;
  }
  // Props are equal, don't re-render
  return true;
};

/**
 * Inner Map component that receives props for memo comparison.
 * State changes (viewportReady, hasMapInstance) will still trigger re-renders,
 * but the memo prevents re-renders from parent/Redux changes that don't affect these specific props.
 */
// eslint-disable-next-line react/display-name
const MapInner = memo(({
  viewport,  // eslint-disable-line no-unused-vars -- used in arePropsEqual memo comparison
  filters,   // eslint-disable-line no-unused-vars -- used in arePropsEqual memo comparison
  agencyId,
  location,
  entityList,
  boundsData,
  drawing
}) => {
  const dispatch = useDispatch();

  // State - viewportReady triggers re-render, mapInstance stored in ref
  const [viewportReady, setViewportReady] = useState(false);
  const [hasMapInstance, setHasMapInstance] = useState(false);

  // Refs
  const mapInstanceRef = useRef(null);
  const viewportReadyRef = useRef(false);
  const prevLocationSearchRef = useRef(location.search);
  const prevBoundsDataRef = useRef(boundsData);

  // Keep ref in sync for debounced function access
  useEffect(() => {
    viewportReadyRef.current = viewportReady;
  }, [viewportReady]);

  const isEntity = useCallback(dataType => {
    return includes(entityList, dataType);
  }, [entityList]);

  // Loads a single entity/group/moratorium on the map
  const loadSingle = useCallback(locationObj => {
    const searchParams = new URLSearchParams(locationObj.search);
    for (const [dataType, value] of searchParams) {
      if (dataType === 'open') {
        if (value === 'shape') {
          dispatch(enableShapeSelection());
        }
      } else if (dataType === 'group') {
        dispatch(viewGroupOnMap(value));
      } else if (isEntity(dataType)) {
        dispatch(viewEntityOnMap(value, dataType));
      } else if (dataType === 'moratorium') {
        dispatch(viewMoratoriumOnMap(value));
      }
    }
  }, [dispatch, isEntity]);

  // updateViewport - debounced, needs ref access
  const updateViewportFn = useCallback(() => {
    const mapInstance = mapInstanceRef.current;
    if (!mapInstance) {
      return;
    }

    renderHighlightCircle();
    dispatch(setViewport('map', mapInstance));

    if (!viewportReadyRef.current) {
      dispatch(setInitialEntityBounds(mapInstance.getBounds()));
      setViewportReady(true);
    }
  }, [dispatch]);

  // Create debounced version once
  const updateViewport = useMemo(
    () => debounce(updateViewportFn, 600),
    [updateViewportFn]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      updateViewport.cancel();
    };
  }, [updateViewport]);

  // onMapLoad - sets up window globals
  const onMapLoad = useCallback(mapInstance => {
    if (!window.mapInstance || !window.highlightCircle) {
      window.highlightCircle = new google.maps.Circle();
    }
    if (mapInstance) {
      // Store instance on the window namespace for faster zoom functionality
      window.mapInstance = mapInstance;
      renderHighlightCircle();
      setMapStreetViewPosition(mapInstance);
    }
    mapInstanceRef.current = mapInstance;
    // Only trigger re-render once when map first loads
    if (mapInstance && !hasMapInstance) {
      setHasMapInstance(true);
    }
  }, [hasMapInstance]);

  const onMeasureDragEnd = useCallback(() => {
    dispatch(setDrawingPath(window.measure.path));
  }, [dispatch]);

  const onMapClick = useCallback(event => {
    const { done, mode } = drawing;
    if (mode !== '') {
      // If we are measuring distances, set the start or end positions
      if (mode === 'measurement' && !done) {
        const position = event.latLng.toJSON();
        if (window.measure && window.measure.interactive) {
          addVertex(position);
        } else {
          initializeMeasurement(position, onMeasureDragEnd, value => dispatch(setDrawingDone(value)));
        }
        dispatch(setDrawingPath(window.measure.path));
      }
      // Don't open anything on the tray if we are drawing a shape
      return;
    }
    const position = {
      lng: event.latLng.lng(),
      lat: event.latLng.lat()
    };
    dispatch(openAreaTray(position));
  }, [dispatch, drawing, onMeasureDragEnd]);

  const onMapDblClick = useCallback(event => {  // eslint-disable-line no-unused-vars
    onInteractiveStop(onMeasureDragEnd, value => dispatch(setDrawingDone(value)));
  }, [dispatch, onMeasureDragEnd]);

  const onMouseMove = useCallback(event => {
    window.highlightCircle.setCenter(event.latLng);
    const { mode } = drawing;
    if (mode === 'measurement') {
      onMeasurementMouseMove(event);
    }
  }, [drawing]);

  // dragstart/dragend workaround for mousemove bug (https://issuetracker.google.com/issues/121006350)
  const onDragStart = useCallback(event => {  // eslint-disable-line no-unused-vars
    window.dragStartCenter = window.mapInstance.getCenter();
  }, []);

  const onDragEnd = useCallback(event => {  // eslint-disable-line no-unused-vars
    const map = window.mapInstance;
    if (window.dragStartCenter && window.dragStartCenter.equals(map.getCenter())) {
      // Execute panBy() if the map has not been moved during dragging
      map.panBy(0, 0);
    }
  }, []);

  // Initial mount - load single entity/group/moratorium
  useEffect(() => {
    loadSingle(location);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for location.search changes
  useEffect(() => {
    if (prevLocationSearchRef.current !== location.search) {
      loadSingle(location);
    }
    prevLocationSearchRef.current = location.search;
  }, [location.search, loadSingle, location]);

  // Watch for boundsData changes
  useEffect(() => {
    const mapInstance = mapInstanceRef.current;
    if (
      prevBoundsDataRef.current !== boundsData &&
      boundsData &&
      boundsData.segments &&
      mapInstance
    ) {
      const bounds = segmentsToBounds(boundsData.segments);
      if (bounds) {
        mapInstance.fitBounds(bounds);
        if (mapInstance.getZoom() > 18) {
          mapInstance.setZoom(18);
        }
      }
    }
    prevBoundsDataRef.current = boundsData;
  }, [boundsData]);

  const showComponents = viewportReady && window.google && (agencyId || isAppAdmin());

  return (
    <div data-testid="main-map" styleName="map-container">
      <div styleName="map-content">
        <Loader />
        <UnstyledHeading rank={2} ariaOnly>Map Tray</UnstyledHeading>
        <TabBar />
        <TabBarToggle />
        <Tray />
        <UnstyledHeading rank={2} ariaOnly>Map Controls</UnstyledHeading>
        {hasMapInstance && <LayersMenu style={{ right: '17.5rem' }} />}
        {hasMapInstance && <Legend />}
        {hasMapInstance && <FloatingBar />}
        <UnstyledHeading rank={2} ariaOnly>Map</UnstyledHeading>
        <DotMapsGoogleMap
          components={showComponents && <Components />}
          onBoundsChanged={updateViewport}
          onClick={onMapClick}
          onDblClick={onMapDblClick}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onMapLoad={onMapLoad}
          onMouseMove={onMouseMove}
        />
        <AddToGroupDialog />
        <MapDateRangeDialog />
        {isWorkflowEnabled() && <StartCycleDialog />}
      </div>
      <FilePreview />
    </div>
  );
}, arePropsEqual);

MapInner.propTypes = {
  agencyId: PropTypes.number,
  boundsData: PropTypes.shape({
    segments: PropTypes.array
  }),
  drawing: PropTypes.shape({
    done: PropTypes.bool,
    mode: PropTypes.string
  }),
  entityList: PropTypes.array,
  filters: PropTypes.object,
  location: PropTypes.shape({
    search: PropTypes.string
  }),
  viewport: PropTypes.object
};

/**
 * Main Map component that connects to Redux and passes props to MapInner.
 * This separation allows React.memo to compare specific props for performance.
 * @returns {React.ReactElement} The Map component.
 */
const Map = () => {
  const location = useLocation();

  // Selectors
  const viewport = useSelector(state => state.map.viewport);
  const filters = useSelector(state => state.map.filters);
  const boundsData = useSelector(state => state.map.boundsData);
  const drawing = useSelector(state => state.map.drawing);
  const entityList = useSelector(state => Object.keys(state.entities.types));
  const agencyId = useSelector(state => state.auth.user.agencyId);

  return (
    <MapInner
      viewport={viewport}
      filters={filters}
      agencyId={agencyId}
      location={location}
      entityList={entityList}
      boundsData={boundsData}
      drawing={drawing}
    />
  );
};

export default Map;
